import { Link, useLocation, useNavigate } from "react-router-dom";
import usePlatformAdmin from '../hooks/usePlatformAdmin';
import { useContext, useState, useEffect, useMemo, useRef, useLayoutEffect, createContext } from "react";
import { INIKRU_LOGO as inikruLogo } from '../utils/brandAssets';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  ShoppingCartIcon,
  Upload,
  CircleUserRound,
  HandHelping,
  LogOut,
  MenuIcon,
  XIcon,
  Search,
  LayoutTemplate,
  Paintbrush,
  Briefcase,
  Combine,
  Package,
  Mail,
  Ticket,
  BarChart3,
  Globe,
  Images,
  Award,
  Repeat,
  Megaphone,
  FileBarChart,
  Warehouse,
  Users,
  Inbox,
  MessageSquare,
  Hammer,
  UserCog,
  Wallet,
  CreditCard,
  Receipt,
  Calculator,
  FolderLock,
  Share2,
  FileText,
  Funnel,
  Rows3,
  CalendarClock,
  PackagePlus,
  Settings,
  Blocks,
  Wand2,
  Tags,
  Tag,
  UsersRound,
  ChevronDown,
  RotateCcw,
  Sun,
  Star,
  Trash2,
} from "lucide-react";
import AuthContext from '../context/AuthContext';
import { isIndia } from '../utils/currency';
import { usePluginContext } from '../context/PluginContext';
import { useSidebarBadges } from '../context/SidebarBadgesContext';
import axios from 'axios';
import dialog from '../utils/dialog';
import Badge from './ui/Badge';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
import { capWord, hasCapability, hasSurface, isOwner, canAccessArea, canUseTools, canManageFeatures, hasCatalogSchema, configVersion, subscribeBusinessConfig, isToolOn, managesStock, isSiteOnly } from '../utils/businessConfig';
import {
  ObjectIcon, loadObjectTypes, objectTypes, objectTypesLoaded, subscribeObjectTypes,
} from '../utils/customObjects';
import { useT } from '../i18n';
import { Skeleton } from './ui/Skeleton';

// Short-lived cache for the subscription-status response. Base.js is rendered
// per page (not a persistent layout route), so Sidebar remounts on every
// navigation and previously refetched /get_subscription_status/ each time.
// Cache the value for a minute so a remount reuses it instead of refetching.
const SUB_CACHE_KEY = 'inikru_sub_status_cache';
const SUB_CACHE_TTL_MS = 60000;

const readSubCache = () => {
  try {
    const raw = sessionStorage.getItem(SUB_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.ts > SUB_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeSubCache = (data) => {
  try {
    sessionStorage.setItem(SUB_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // sessionStorage unavailable (private mode / quota) — skip caching.
  }
};

// Collapsed/expanded state of the sidebar section groups, persisted so the
// designer's preferred layout survives navigation and reloads. A group that
// contains the ACTIVE route is always forced open.
//
// `_v2` because the seven groups became five and every id changed
// (products/orders/marketing/production/finance/store/account →
// sell/catalog/customers/money/store). Reusing the old key would have left a
// merchant with collapse flags for groups that no longer exist — and, worse,
// the surviving `store` id carrying a collapsed state from a group that used
// to hold five items and now holds sixteen.
const COLLAPSE_KEY = 'inikru_sidebar_collapsed_v2';

// A business with everything switched on renders ~40 nav items. Opening every
// group by default meant the useful half was below the fold on a laptop, so the
// FIRST thing a new merchant did was scroll. Default to all-collapsed instead:
// `NavGroup` force-opens whichever group holds the current route, so what you
// actually get is "the group you're in, open; the rest, one click away".
// Only applies when the merchant has no stored preference — once they toggle
// anything, their own choice is what persists.
const ALL_GROUP_IDS = ['sell', 'catalog', 'customers', 'money', 'store'];
// …except SELL, which stays open by default: on /today no group holds the
// route, and a first login that shows nothing but five collapsed grey words
// hides the whole product. One open group = instant orientation, no scroll.
const DEFAULT_COLLAPSED = Object.fromEntries(
  ALL_GROUP_IDS.map((id) => [id, id !== 'sell']));
const readCollapsedGroups = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(COLLAPSE_KEY));
    return stored && typeof stored === 'object' ? stored : { ...DEFAULT_COLLAPSED };
  } catch {
    return { ...DEFAULT_COLLAPSED };
  }
};

// The scroll position of the nav, kept at MODULE scope on purpose.
// All 100 portal pages render their own `<Base>`, which renders its own
// `<Sidebar>` — so navigating anywhere unmounts and remounts this component and
// the browser has no scroll position left to restore. React state and refs die
// with the instance; a module variable outlives it and resets on a real page
// load, which is exactly the lifetime we want. (The proper fix is one <Base>
// hoisted into a layout route, but that is a 100-file change.)
let savedNavScrollTop = 0;

// Typing in the filter box hides non-matching items. Shared by context because
// the nav is authored as inline JSX, not a data array — every NavItem asks
// "does my label match?" itself.
const SidebarFilterContext = createContext('');

// Small red pill for unread / pending counts. Hidden when count is 0.
const CountBadge = ({ count }) => {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  const display = n > 99 ? '99+' : String(n);
  return (
    <span
      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold text-white bg-red-500 shadow-sm"
      aria-label={`${display} unread`}
    >
      {display}
    </span>
  );
};

export default function Sidebar() {
  const { user, authToken, logoutUser } = useContext(AuthContext);
  const {
    isPluginEnabled,
    loading: pluginsLoading,
  } = usePluginContext();
  const sidebarBadges = useSidebarBadges();
  // Every visible label goes through `t()` (src/i18n). English is a
  // passthrough of the fallback, so nothing here reads differently until a
  // merchant switches language — and then the nav re-renders in place.
  const t = useT();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(readCollapsedGroups);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false,
    expiry_date: null,
  });

  // Nav filter. `navQuery` is what the merchant typed; the trimmed lowercase
  // form is what items match against.
  const [navQuery, setNavQuery] = useState('');
  const filter = navQuery.trim().toLowerCase();

  // Restore the nav's scroll position before paint, so a remount is invisible
  // rather than a visible jump to the top. useLayoutEffect, not useEffect —
  // with useEffect the browser paints at scrollTop 0 first and it flickers.
  const navRef = useRef(null);
  useLayoutEffect(() => {
    const el = navRef.current;
    if (el && savedNavScrollTop) el.scrollTop = savedNavScrollTop;
  }, []);
  const handleNavScroll = (e) => {
    savedNavScrollTop = e.currentTarget.scrollTop;
  };

  const toggleGroup = (id) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
      return next;
    });
  };
  const location = useLocation();
  const navigate = useNavigate();

  // Memoized so we reuse one axios instance across renders/remounts instead of
  // creating a fresh `axios.create()` every render.
  const authAxios = useMemo(
    () =>
      axios.create({
        baseURL: API_BASE_URL,
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
        },
      }),
    [authToken?.access]
  );

  const fetchSubscriptionStatus = async () => {
    if (!authToken) return;
    try {
      const response = await authAxios.get('/api/get_subscription_status/');
      setSubscriptionStatus(response.data);
      writeSubCache(response.data);
    } catch (err) {
      devLog.error('Failed to fetch subscription status:', err);
      setSubscriptionStatus({ is_active: false, expiry_date: null });
      if (err.response?.status === 401) {
        dialog.fire({
          title: t('sub.session_expired', 'Session Expired'),
          text: t('sub.login_again', 'Please login again.'),
          icon: 'warning',
          confirmButtonColor: "#B40A0A",
        }).then(() => {
          logoutUser();
        });
      }
    }
  };

  useEffect(() => {
    if (!authToken) return;
    // Reuse a fresh-enough cached value on remount instead of refetching.
    const cached = readSubCache();
    if (cached) {
      setSubscriptionStatus(cached);
      return;
    }
    fetchSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // The Tools page switches features in a module-level config, not React state.
  // Listen so the nav updates the moment something is switched on or off.
  const [cfgVersion, setCfgVersion] = useState(configVersion);
  useEffect(() => {
    // Compare on mount as well as subscribing — the config often lands before
    // this component exists, and a missed update leaves the wrong nav on screen.
    if (configVersion() !== cfgVersion) setCfgVersion(configVersion());
    const unsubscribe = subscribeBusinessConfig(setCfgVersion);
    const onToolsChanged = () => setCfgVersion(configVersion());
    window.addEventListener('inikru:tools-changed', onToolsChanged);
    return () => {
      unsubscribe();
      window.removeEventListener('inikru:tools-changed', onToolsChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname]);

  // ── Custom objects (Studio) ─────────────────────────────────────────────
  // One nav item per business noun an admin invented, injected into the group
  // the DEFINITION names (`nav.group`), ordered by its `nav.order`.
  //
  // Fetched ONCE PER PAGE LOAD, at module scope (`utils/customObjects`). This
  // component unmounts and remounts on every route change — all ~100 portal
  // pages render their own `<Base>` — so a plain `useEffect` fetch here would
  // fire a request on every navigation, which is the same reason the nav's
  // scroll position lives in a module variable.
  //
  // 🔴 IF THE FETCH FAILS, THE STUDIO FLAG IS OFF (a 404), OR THERE ARE NONE,
  // this list is `[]` and every line below renders nothing — the sidebar is
  // byte-for-byte what it is today. That is the whole contract; nothing here
  // may ever show an error, a placeholder or an empty heading.
  const [objectNav, setObjectNav] = useState(objectTypes);
  useEffect(() => {
    if (!authToken) return undefined;
    // Compare on mount as well as subscribing: the list can land before this
    // instance exists, and a missed update leaves the items off the nav.
    if (objectTypesLoaded()) setObjectNav(objectTypes());
    const unsubscribe = subscribeObjectTypes(setObjectNav);
    loadObjectTypes();
    return unsubscribe;
  }, [authToken]);

  // Gated with `canAccessArea(area)` — the SAME helper every other item uses,
  // never a new one. The server has already filtered this list by the acting
  // role (`object_list_types` does the filtering rather than returning
  // everything and hiding it client-side), so this is the second half of a
  // belt-and-braces pair and cannot be the only thing standing between a
  // cashier and an area.
  const objectsByGroup = useMemo(() => {
    const map = { sell: [], catalog: [], customers: [], money: [], store: [] };
    (objectNav || [])
      .filter((o) => o && o.slug && canAccessArea(o.area))
      .forEach((o) => {
        // A group that is not one of the five lands in Sell — the group the
        // packs' own business nouns (reservations, kitchen tickets) already
        // declare. A findable item in a plausible place beats one that no
        // group renders and nobody can reach.
        const group = map[o.group] ? o.group : 'sell';
        map[group].push(o);
      });
    Object.keys(map).forEach((k) => map[k].sort(
      (a, b) => (a.order - b.order) || String(a.label).localeCompare(String(b.label)),
    ));
    return map;
    // `cfgVersion` is in here on purpose: `canAccessArea` reads the business
    // config singleton, so a config that lands after login must re-filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectNav, cfgVersion]);

  // Every item listed in the designer's plugin catalog is gated. If the
  // plugin is not installed, the sidebar entry disappears and the route
  // shows an "install required" panel instead of the page.
  const p = {
    dailyOps: isPluginEnabled('daily_ops'),
    inventory: isPluginEnabled('inventory'),
    unifiedInbox: isPluginEnabled('unified_inbox'),
    whatsapp: isPluginEnabled('whatsapp'),
    bidRequests: isPluginEnabled('bid_requests'),
    customers: isPluginEnabled('customers'),
    productionBoard: isPluginEnabled('production_board'),
    team: isPluginEnabled('team'),
    payments: isPluginEnabled('payments'),
    expenses: isPluginEnabled('expenses'),
    suppliers: isPluginEnabled('suppliers'),
    reportPnl: isPluginEnabled('report_pnl'),
    reportGstr1: isPluginEnabled('report_gstr1'),
    reportGoals: isPluginEnabled('report_goals'),
    automations: isPluginEnabled('automations'),
    emailMarketing: isPluginEnabled('email_marketing'),
  };
  // Structure belongs to the platform admin; content to the designer.
  const isPlatformAdmin = usePlatformAdmin();

  // ── The gate behind every nav item, named once ──────────────────────────
  //
  // Seven groups became five (UI_CONSOLIDATION_PLAN phase 3), which means the
  // Marketing / Production / Finance GROUP gates disappeared as containers.
  // A group gate is a condition too, so each one is folded into the items it
  // used to wrap — an item that only appeared because its group let it through
  // must not become visible now that the group is gone:
  //
  //   Finance    `anyMoneyEnabled` ⇒ canAccessArea('money') for every item
  //              inside it. Folded into money.* below (each of those reads
  //              `moneyArea && …`). The Finance group could ALSO open via
  //              `hasSurface('connect_payments') && canAccessArea('money')`,
  //              which likewise implies the money area — so `moneyArea` is a
  //              faithful, not a stricter, replacement.
  //   Production `anyProductionEnabled` = p.productionBoard || p.team, i.e.
  //              exactly the OR of the two items it held. Folding it changes
  //              nothing (X && (X || Y) ≡ X), so both items keep their gate.
  //   Marketing  `p.emailMarketing && hasSurface('marketing') &&
  //              canAccessArea('marketing')` — the whole expression lives on
  //              inside the Campaigns hub gate's email clause.
  //   Store      `(storefront_design || analytics) && canAccessArea('settings')`
  //              ⇒ canAccessArea('settings') on Website/Themes/Pages/SEO/
  //              Analytics, which now sit in a group that is not gated.
  //
  // Groups themselves are now shown when ANY of their items is — an OR of the
  // item gates, which hides an empty heading without hiding anything else.
  const moneyArea = canAccessArea('money');
  const settingsArea = canAccessArea('settings');
  const storefront = hasSurface('storefront_design');
  // Recomputed per mount from the config singleton (Sidebar remounts on every
  // route change), and the configVersion subscription re-renders when the
  // fresh config lands after login — never convert this to one-shot state.
  const siteOnly = isSiteOnly();

  const gate = {
    // ── SELL ──
    counter: hasCapability('counter_billing'),
    orderBoard: hasCapability('pay_later_orders') || hasCapability('counter_billing'),
    appointments: hasCapability('appointments'),
    onlineOrders: hasSurface('online_orders'),
    allOrders: p.unifiedInbox && hasSurface('online_orders'),
    whatsapp: p.whatsapp,
    bidRequests: p.bidRequests,
    messages: storefront,

    // ── CATALOG ──
    catalogList: hasCatalogSchema(),
    // The hub shows whichever of Stock / Batches / Serials / Locations /
    // Stock Take / Ageing this business has, so the nav asks the same OR.
    inventory: p.inventory || hasCapability('batch_expiry') || hasCapability('serial_tracking')
      || hasCapability('multi_location') || hasCapability('stock_take') || managesStock(),
    recipes: hasCapability('bom_yield'),
    merchandising: hasSurface('collections'),
    discounts: hasSurface('coupons'),
    catalogSettings: !hasCatalogSchema(),

    // ── CUSTOMERS ──
    customers: p.customers || hasCapability('b2b_pricing') || hasCapability('price_lists'),
    engagement: isToolOn('loyalty') || isToolOn('vouchers') || p.customers,
    // The full OR the deleted Marketing group used to enforce, so email
    // marketing still needs its surface AND its area, not just its plugin.
    campaigns: isToolOn('campaigns')
      || (p.emailMarketing && hasSurface('marketing') && canAccessArea('marketing'))
      || p.automations,
    subscriptions: isToolOn('subscriptions'),
    returns: hasSurface('online_orders'),
    leads: hasCapability('crm_pipeline'),
    quotes: hasCapability('b2b_pipeline'),

    // ── MONEY ── (every one carries the old Finance group's money-area check)
    // `isToolOn('reminders')` is in the OR because the Money-In Overview tab IS
    // the old Reminders page (aged receivables + the cadence/UPI settings on its
    // gear) and `/reminders` redirects here. Without it, a business whose only
    // money feature is reminders — brewbite, the café — had the tool switched on
    // and no way to reach it. Found by the Phase-5 persona sweep; the hub's own
    // tab-visibility rule carries the matching clause.
    moneyIn: moneyArea && (p.payments || hasCapability('khata') || hasSurface('connect_payments')
      || isToolOn('reminders')),
    connectPayments: hasSurface('connect_payments') && moneyArea,
    books: moneyArea && hasCapability('ca_export'),
    billingDocs: isToolOn('estimates') || isToolOn('challans') || isToolOn('returns'),
    expenses: p.expenses && moneyArea,
    suppliers: p.suppliers && moneyArea,
    // 🔴 `moneyArea &&` is deliberate: P&L / GSTR-1 / Goals / Commerce all sat
    // inside the Finance group, so a cashier never saw them. Dropping it with
    // the group would have handed the P&L to every role.
    reports: moneyArea && (p.reportPnl || (p.reportGstr1 && isIndia()) || p.reportGoals
      || isToolOn('returns') || isToolOn('loyalty') || isToolOn('vouchers') || isToolOn('campaigns')),

    // ── STORE & SETUP ──
    website: storefront && settingsArea,
    themes: isPlatformAdmin && storefront && settingsArea,
    analytics: hasSurface('analytics') && settingsArea,
    productionBoard: p.productionBoard,
    karigars: p.team,
    portfolio: hasCapability('portfolio'),
    storeSettings: storefront && settingsArea,
    features: canManageFeatures(),
    personalize: canUseTools() && isOwner(),
    teamRoles: hasCapability('staff_logins') && isOwner(),
    documents: hasCapability('documents') && settingsArea,
    // NOTE: no `approvals` gate here on purpose — Approvals moved to the top-bar
    // bell (4B), which carries the identical `hasCapability('approvals')` check.
  };

  // `pluginsLoading ||` on each: every plugin gate reads false until the
  // enabled-plugins list lands, so without it a group would be hidden on first
  // paint and then pop in — the skeleton inside exists precisely to avoid that.
  // A group is shown when ANY of its items is — and a custom object IS one of
  // its items, so a group holding nothing but custom objects still opens. With
  // no custom objects each of these terms is `false` and the expressions are
  // exactly what they were.
  const showSell = pluginsLoading || gate.counter || gate.orderBoard || gate.appointments
    || gate.onlineOrders || gate.allOrders || gate.whatsapp || gate.bidRequests || gate.messages
    || objectsByGroup.sell.length > 0;
  const showCustomers = pluginsLoading || gate.customers || gate.engagement || gate.campaigns
    || gate.subscriptions || gate.returns || gate.leads || gate.quotes
    || objectsByGroup.customers.length > 0;
  const showMoney = pluginsLoading || gate.moneyIn || gate.connectPayments || gate.books
    || gate.billingDocs || gate.expenses || gate.suppliers || gate.reports
    || objectsByGroup.money.length > 0;

  const sidebarSharedClasses = "fixed inset-y-0 left-0 flex flex-col w-[260px] bg-white border-r border-line shadow-[0_20px_44px_-36px_rgba(22,19,15,0.4)] transform transition-all duration-300 ease-in-out";
  const mobileHiddenClasses = "-translate-x-full";
  const mobileVisibleClasses = "translate-x-0";
  const desktopVisibleClasses = "lg:translate-x-0 lg:shadow-none lg:border-line";

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const toggleButtonBaseClasses = "hidden fixed top-4 p-2 bg-white border border-line rounded-full text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A] transition-all duration-300 ease-in-out shadow-[0_12px_28px_-18px_rgba(22,19,15,0.5)]";
  const toggleButtonClosedPosition = "left-4";
  const toggleButtonOpenPosition = "left-[200px] top-2 border-transparent shadow-none";

  const handleUploadClick = () => {
    // The trial auto-starts at registration, so a locked Upload almost always
    // means it EXPIRED — only legacy accounts may still need to start it.
    const trialUsed = !!subscriptionStatus.has_used_free_trial;
    dialog.fire({
        title: trialUsed ? t('sub.trial_ended_title', 'Your free trial has ended') : t('sub.start_trial_title', 'Start your free trial'),
        text: trialUsed
          ? t('sub.trial_ended_text', 'Renew your plan to keep uploading products and selling on your store.')
          : t('sub.start_trial_text', 'Start your free 15-day trial to upload products and build your store.'),
        icon: 'info',
        confirmButtonText: trialUsed ? t('sub.renew_now', 'Renew now') : t('sub.start_trial', 'Start free trial'),
        confirmButtonColor: "#B40A0A",
        showCancelButton: true,
        cancelButtonText: t('action.later', 'Later'),
    }).then((result) => {
        if (result.isConfirmed) {
            navigate('/store-settings#billing');
        }
    });
  };

  const closeMenuIfOpen = () => isMobileMenuOpen && setIsMobileMenuOpen(false);

  // The nav items for one group, and the routes that should force it open.
  // `/o/<slug>` covers `/o/<slug>/5` too — NavGroup matches a prefix.
  const objectItems = (group) => (objectsByGroup[group] || []).map((o) => (
    <NavItem
      key={o.slug}
      icon={<ObjectIcon name={o.icon} size={20} />}
      label={t(`nav.object.${o.slug}`, o.label || o.name_plural || o.name)}
      to={`/o/${o.slug}`}
      currentPath={location.pathname}
      closeMobileMenu={closeMenuIfOpen}
    />
  ));
  const objectPaths = (group) => (objectsByGroup[group] || []).map((o) => `/o/${o.slug}`);

  // Skeleton placeholder while the enabled-plugins list is resolving.
  const GroupSkeleton = ({ rows = 2 }) => (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 rounded-[10px]" />
      ))}
    </div>
  );

  return (
    <>
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          toggleMobileMenu();
        }}
        className={`${toggleButtonBaseClasses} ${isMobileMenuOpen ? toggleButtonOpenPosition : toggleButtonClosedPosition} touch-manipulation`}
        style={{
          zIndex: 'var(--z-sidebar-toggle)',
          minHeight: '44px',
          minWidth: '44px',
          pointerEvents: 'auto'
        }}
        aria-label={isMobileMenuOpen ? t('nav.close_menu', 'Close menu') : t('nav.open_menu', 'Open menu')}
        aria-expanded={isMobileMenuOpen}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isMobileMenuOpen ? <XIcon size={24} /> : <MenuIcon size={24} />}
      </motion.button>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-black/40 lg:hidden touch-manipulation"
            style={{
              zIndex: 'var(--z-modal-backdrop)',
              pointerEvents: 'auto'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>

      <motion.aside
        className={`lg:w-[270px] ${sidebarSharedClasses} ${isMobileMenuOpen ? mobileVisibleClasses : mobileHiddenClasses} ${desktopVisibleClasses}`}
        style={{ zIndex: 'var(--z-modal)', borderRadius: 0 }}
        aria-label="Sidebar"
        initial={false}
        animate={isMobileMenuOpen ? "open" : "closed"}
      >
        <div className="flex items-center justify-between h-16 px-5 border-b border-line shrink-0" style={{ borderRadius: 0 }}>
          <Link to={siteOnly ? '/design' : '/homepage'} className="flex items-center" onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)} aria-label={t('nav.home_aria', 'Inikru home')}>
            <img src={inikruLogo} alt="Inikru" className="h-[18px] w-auto block" />
          </Link>
        </div>

        {/* Filter box. With ~40 items an "everything on" business scrolls past
            most of them; typing three letters is faster than hunting, and it
            searches ACROSS collapsed groups (NavGroup force-opens while a
            filter is active) so a match is never hidden behind a chevron. */}
        <div className="px-4 pt-3 pb-1 shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setNavQuery(''); } }}
              placeholder={t('nav.filter_menu', 'Filter menu…')}
              aria-label={t('nav.filter_menu', 'Filter menu…')}
              className="w-full pl-8 pr-7 py-1.5 text-[13px] rounded-[8px] border border-line bg-white text-ink placeholder:text-gray-400 focus:outline-none focus:border-ink"
            />
            {navQuery && (
              <button
                type="button"
                onClick={() => setNavQuery('')}
                aria-label={t('nav.clear_filter', 'Clear filter')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-ink focus:outline-none"
              >
                <XIcon size={13} />
              </button>
            )}
          </div>
        </div>

        <SidebarFilterContext.Provider value={filter}>
        <nav
          ref={navRef}
          onScroll={handleNavScroll}
          className="flex-grow p-4 pt-2 overflow-y-auto custom-scrollbar sidebar-nav"
          data-filtering={filter ? 'yes' : 'no'}
          style={{ borderRadius: 0 }}
        >
          {siteOnly ? (
            /* ══ SITE MODE ══ — a website-not-a-shop account (the portfolio
               trade). The whole portal is their site, so the nav is exactly
               that: the site, its numbers, their account. This is a SEPARATE
               layout, not dissolved merchant gates — every merchant group
               below renders byte-identically when siteOnly is false, and a
               stale config without the key fails to the merchant view. */
            <>
              <div className="space-y-1">
                <NavItem
                  icon={<Sun size={20} />}
                  label={t('nav.today', 'Today')}
                  to="/today"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              </div>
              <NavGroup
                id="site"
                title={t('nav.group.site', 'My Website')}
                paths={["/design", "/template-builder", "/visual-editor", "/portfolio", "/seo-settings"]}
                currentPath={location.pathname}
                collapsedMap={collapsedGroups}
                onToggle={toggleGroup}
              >
                <NavItem
                  icon={<Paintbrush size={20} />}
                  label={t('nav.edit_site', 'Edit my site')}
                  to="/design"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
                {/* UNGATED on purpose — the signup baseline for this trade is
                    ('storefront',) alone, so gate.portfolio (the capability)
                    is false for exactly the people whose centrepiece this is. */}
                <NavItem
                  icon={<Share2 size={20} />}
                  label={t('nav.my_work', 'My Work')}
                  to="/portfolio"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
                <NavItem
                  icon={<Globe size={20} />}
                  label={t('nav.seo', 'SEO')}
                  to="/seo-settings"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              </NavGroup>
              <NavGroup
                id="growth"
                title={t('nav.group.growth', 'Growth')}
                paths={["/analytics"]}
                currentPath={location.pathname}
                collapsedMap={collapsedGroups}
                onToggle={toggleGroup}
              >
                <NavItem
                  icon={<BarChart3 size={20} />}
                  label={t('nav.analytics', 'Analytics')}
                  to="/analytics"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              </NavGroup>
              <NavGroup
                id="account"
                title={t('nav.group.account', 'Account')}
                paths={["/Profile", "/store-settings", "/Contact"]}
                currentPath={location.pathname}
                collapsedMap={collapsedGroups}
                onToggle={toggleGroup}
              >
                <NavItem
                  icon={<CircleUserRound size={20} />}
                  label={t('nav.profile', 'Profile')}
                  to="/Profile"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
                <NavItem
                  icon={<CreditCard size={20} />}
                  label={t('nav.plan_billing', 'Plan & billing')}
                  to="/store-settings#billing"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
                <NavItem
                  icon={<Mail size={20} />}
                  label={t('nav.support', 'Support')}
                  to="/Contact"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              </NavGroup>
            </>
          ) : (
          <>
          {/* ── Today / Dashboard ── */}
          <div className="space-y-1">
            {/* The post-login landing (UI_CONSOLIDATION 4A). First, and above
                every group, because it is the one screen that answers "what
                needs me right now" across all five of them. Ungated on purpose:
                the SERVER decides which cards a business can receive, so there
                is no gate here to drift out of step with it. */}
            <NavItem
              icon={<Sun size={20} />}
              label={t('nav.today', 'Today')}
              to="/today"
              currentPath={location.pathname}
              closeMobileMenu={closeMenuIfOpen}
            />
            {/* 🔴 "Products" must open the PRODUCTS. It pointed at /homepage,
                which is a dashboard carrying store settings — a schema business
                clicking it got working-mode toggles, not its catalogue. Fashion
                has no /catalog (its list IS /homepage, see App.js CatalogEntry),
                so the destination follows the same one question every other
                catalogue link asks. */}
            <NavItem
              icon={<HomeIcon size={20} />}
              label={t('nav.products', capWord('products', 'Products'))}
              to={hasCatalogSchema() ? '/catalog' : '/homepage'}
              currentPath={location.pathname}
              closeMobileMenu={closeMenuIfOpen}
            />
            {/* Day Summary was removed 2026-08-29: it answered the same question
                as Today with a second set of near-identical numbers, and its own
                copy of the storefront toggle. Its widgets now live in Today's
                "How today is going" band; /dashboard redirects there. */}
            {/* Plugin Store intentionally NOT in the nav (owner decision,
                2026-07-11) — /plugins stays reachable via PluginGuard links. */}
          </div>

          {/* ══ SELL ══ — everything that takes an order, in the order a day
              actually runs: the counter, what is being prepared, the diary,
              then the channels orders arrive through. */}
          {showSell && (
          <NavGroup
            id="sell"
            title={t('nav.group.sell', 'Sell')}
            paths={["/counter", "/order-board", "/preorders", "/appointments", "/Orders", "/orders-unified", "/orderbook", "/whatsapp", "/Proposals", "/messages", ...objectPaths('sell')]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              {/* A3 — Counter Billing (POS). */}
              {gate.counter && (
                <NavItem
                  icon={<Receipt size={20} />}
                  label={t('nav.counter', capWord('counter', 'Counter Billing'))}
                  to="/counter"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Live kanban over counter/QR orders, with Pre-orders as its
                  second tab — both answer "what am I preparing". */}
              {gate.orderBoard && (
                <NavItem
                  icon={<Rows3 size={20} />}
                  label={t('nav.order_board', 'Order Board')}
                  to="/order-board"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Bookable services calendar — salons / clinics. */}
              {gate.appointments && (
                <NavItem
                  icon={<CalendarClock size={20} />}
                  label={t('nav.appointments', 'Appointments')}
                  to="/appointments"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Web orders only — a salon that books appointments should not
                  be shown an empty web-orders screen it can never fill. */}
              {gate.onlineOrders && (
                <NavItem
                  icon={<ShoppingCartIcon size={20} />}
                  label={t('nav.website_orders', `Website ${capWord('orders', 'Orders')}`)}
                  to="/Orders"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {pluginsLoading ? (
                <GroupSkeleton rows={3} />
              ) : (
                <>
                  {/* Every channel in one queue. Named "All Orders" because
                      "Unified Inbox" described our plumbing, not their job. */}
                  {gate.allOrders && (
                    <NavItem
                      icon={<Inbox size={20} />}
                      label={t('nav.all_orders', `All ${capWord('orders', 'Orders')}`)}
                      to="/orders-unified"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                      badge={<CountBadge count={sidebarBadges.unifiedOrders} />}
                    />
                  )}
                  {gate.whatsapp && (
                    <NavItem
                      icon={<MessageSquare size={20} />}
                      label={t('nav.whatsapp', 'WhatsApp')}
                      to="/whatsapp"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                      badge={<CountBadge count={sidebarBadges.whatsapp} />}
                    />
                  )}
                  {gate.bidRequests && (
                    <NavItem
                      icon={<HandHelping size={20} />}
                      label={t('nav.custom_requests', 'Custom Requests')}
                      to="/Proposals"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                      badge={<CountBadge count={sidebarBadges.bidRequests} />}
                    />
                  )}
                </>
              )}
              {gate.messages && (
                <NavItem
                  icon={<Mail size={20} />}
                  label={t('nav.enquiries', 'Enquiries')}
                  to="/messages"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {objectItems('sell')}
          </NavGroup>
          )}

          {/* ══ CATALOG ══ — what you sell, what you hold, and how it is
              presented and priced. */}
          <NavGroup
            id="catalog"
            // Distinct from the top-level item that carries the bare trade
            // word — "Products" appearing twice (item AND group header) read
            // as a duplicate in the audit.
            title={t('nav.group.catalog', `${capWord('products', 'Products')} & Stock`)}
            paths={["/UploadProduct", "/catalog", "/inventory", "/batches", "/serials", "/locations", "/stock-take", "/labels", "/reports/stock-ageing", "/recipes", "/item-recipes", "/merchandising", "/collections-manager", "/shop-gallery", "/quizzes", "/categories", "/discounts", "/my-coupons", "/sales", "/catalog-settings", ...objectPaths('catalog')]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              <NavItem
                icon={<Upload size={20} />}
                label={t('nav.add_product', `Add ${capWord('product', 'Product')}`)}
                to="/UploadProduct"
                currentPath={location.pathname}
                closeMobileMenu={closeMenuIfOpen}
                disabled={!subscriptionStatus.is_active}
                onDisabledClick={handleUploadClick}
                badge={!subscriptionStatus.is_active ? <Badge variant="warning" size="sm">{t('nav.pro', 'Pro')}</Badge> : null}
              />
              {/* The product LIST for schema-driven businesses (/catalog →
                  SchemaCatalog). Fashion never sees it: its list lives on the
                  dashboard, and its edit form is /EditProduct/:id. Same gate as
                  App.js's CatalogEntry — `hasCatalogSchema()` is the one question
                  that decides which catalog a business has — so the nav can never
                  disagree with what the route renders. */}
              {gate.catalogList && (
                <NavItem
                  icon={<Package size={20} />}
                  label={t('nav.all_products', `All ${capWord('products', 'Products')}`)}
                  to="/catalog"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {pluginsLoading ? (
                <GroupSkeleton rows={1} />
              ) : (
                /* One destination for stock: Stock · Batches & Expiry ·
                   Serials · Locations · Stock Take · Ageing, plus Barcode
                   Labels as an action rather than a page. */
                gate.inventory && (
                  <NavItem
                    icon={<Warehouse size={20} />}
                    label={t('nav.inventory', 'Inventory')}
                    to="/inventory"
                    currentPath={location.pathname}
                    closeMobileMenu={closeMenuIfOpen}
                  />
                )
              )}
              {/* A8 — Recipes & yield. No hub absorbed these two; they keep
                  their own entries and their own routes.

                  🔴 The suffix is OUTSIDE capWord on purpose. `capWord(key,
                  fallback)` returns the TRADE'S WORD when it has one and drops
                  the fallback entirely — so for any trade whose word for `bom`
                  is "recipes" (a bakery, a beauty brand), this item rendered
                  the bare word "Recipes" and became indistinguishable from the
                  item-recipes entry below it: two identical labels, same icon,
                  different routes. Appending here keeps them apart whatever
                  the trade calls a BOM ("Formula & Yield", "Recipes & Yield"). */}
              {gate.recipes && (
                <NavItem
                  icon={<Combine size={20} />}
                  label={t('nav.recipes', `${capWord('bom', 'Recipe')} & Yield`)}
                  to="/recipes"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* P2.4 — Item recipes (auto-deduct ingredients on sale). */}
              {gate.recipes && (
                <NavItem
                  icon={<Combine size={20} />}
                  label={t('nav.item_recipes', 'Item Recipes')}
                  to="/item-recipes"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Collections · Shop the Look · Product Finders — three ways of
                  arranging the same shelf, so one destination. */}
              {gate.merchandising && (
                <NavItem
                  icon={<Images size={20} />}
                  label={t('nav.merchandising', 'Merchandising')}
                  to="/merchandising"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              <NavItem
                icon={<Tag size={20} />}
                label={t('nav.categories', 'Categories')}
                to="/categories"
                currentPath={location.pathname}
                closeMobileMenu={closeMenuIfOpen}
              />
              {/* Coupons and Sales both answer "make this cheaper", and an
                  owner should not have to know which mechanism we call it. */}
              {gate.discounts && (
                <NavItem
                  icon={<Ticket size={20} />}
                  label={t('nav.discounts', 'Discounts')}
                  to="/discounts"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* "Catalog" = fashion fabric-care + size/upload defaults. Only for
                  garment businesses on the bespoke form; a rice mill / café uses
                  its own schema catalog and has no use for fabric-care. */}
              {gate.catalogSettings && (
                <NavItem
                  icon={<Tags size={20} />}
                  label={t('nav.catalog_settings', 'Catalog Settings')}
                  to="/catalog-settings"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {objectItems('catalog')}
          </NavGroup>

          {/* ══ CUSTOMERS ══ — who buys, how you keep them, and the pipelines
              of people who have not bought yet. */}
          {showCustomers && (
          <NavGroup
            id="customers"
            title={t('nav.group.customers', 'Customers')}
            paths={["/customers", "/customer-segments", "/b2b-buyers", "/price-lists", "/engagement", "/loyalty", "/vouchers", "/referrals", "/campaigns", "/email-marketing", "/automations", "/subscriptions", "/returns", "/leads", "/quotes", ...objectPaths('customers')]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              {pluginsLoading ? (
                <GroupSkeleton rows={4} />
              ) : (
                <>
                  {/* Customers · Segments · B2B Buyers · Price Lists. The gate
                      is the OR of all four: a wholesaler with only b2b_pricing
                      still has a customers page worth opening. */}
                  {gate.customers && (
                    <NavItem
                      icon={<Users size={20} />}
                      label={t('nav.customers', 'Customers')}
                      to="/customers"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* What customers say about the shop — sits beside Customers
                      because it IS customer contact, and carries the same area
                      gate the server applies (`CanAccessCustomers`), so the
                      people who field the complaint can answer it. */}
                  {gate.customers && (
                    <NavItem
                      icon={<Star size={20} />}
                      label={t('nav.reviews', 'Reviews')}
                      to="/reviews"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Loyalty · Gift Vouchers · Referrals — the reasons a
                      customer comes back. */}
                  {gate.engagement && (
                    <NavItem
                      icon={<Award size={20} />}
                      label={t('nav.loyalty', 'Loyalty & Rewards')}
                      to="/engagement"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* WhatsApp · Email · Automations — one destination instead of
                      a split by channel. Carries the conditions the deleted
                      Marketing group used to enforce on Email Marketing. */}
                  {gate.campaigns && (
                    <NavItem
                      icon={<Megaphone size={20} />}
                      label={t('nav.campaigns', 'Campaigns')}
                      to="/campaigns"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* D2C — customer subscriptions ("subscribe & save"). */}
                  {gate.subscriptions && (
                    <NavItem
                      icon={<Repeat size={20} />}
                      label={t('nav.subscriptions', 'Subscriptions')}
                      to="/subscriptions"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* The queue of returns/exchanges SHOPPERS raised on the
                      storefront (pages/Returns). Distinct from the Credit Notes
                      tab of Billing Documents, which is the document the owner
                      writes when they hand the money back — different feature,
                      different table. Shown wherever the business actually
                      sells online, because that is the only place a shopper can
                      raise one. */}
                  {gate.returns && (
                    <NavItem
                      icon={<RotateCcw size={20} />}
                      label={t('nav.returns', 'Returns')}
                      to="/returns"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Wave 2 — CRM leads pipeline. A pipeline is a different
                      motion from a customer list, so it stays its own page. */}
                  {gate.leads && (
                    <NavItem
                      icon={<Funnel size={20} />}
                      label={t('nav.leads', 'Leads')}
                      to="/leads"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Wave 2 — B2B quotations → sales orders. */}
                  {gate.quotes && (
                    <NavItem
                      icon={<FileText size={20} />}
                      label={t('nav.quotations', 'Quotations')}
                      to="/quotes"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                </>
              )}
              {objectItems('customers')}
          </NavGroup>
          )}

          {/* ══ MONEY ══ — what is owed to you, what you owe, and what the
              accountant needs. Every item still carries the money-area check
              the deleted Finance group used to apply on their behalf, so a
              cashier sees exactly as little as before. */}
          {showMoney && (
          <NavGroup
            id="money"
            title={t('nav.group.money', 'Money')}
            paths={["/money", "/payments", "/khata", "/earnings", "/reminders", "/connect-payments", "/books", "/daybook", "/bank-reconciliation", "/compliance", "/billing-documents", "/estimates", "/challans", "/credit-notes", "/expenses", "/suppliers", "/purchases", "/reports", ...objectPaths('money')]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              {pluginsLoading ? (
                <GroupSkeleton rows={3} />
              ) : (
                <>
                  {/* Receivables in one answer: Overview · Payments · Khata ·
                      Earnings. "Remind" is a row action on the first three, not
                      a page of its own any more. */}
                  {gate.moneyIn && (
                    <NavItem
                      icon={<Wallet size={20} />}
                      label={t('nav.payments_dues', 'Payments & Dues')}
                      to="/money"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Getting paid at all — the designer's own Razorpay keys. */}
                  {gate.connectPayments && (
                    <NavItem
                      icon={<CreditCard size={20} />}
                      label={t('nav.payment_setup', 'Payment Setup')}
                      to="/connect-payments"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Statements · Day Book · Bank Reconciliation · Compliance —
                      everything the CA asks for. */}
                  {gate.books && (
                    <NavItem
                      icon={<Calculator size={20} />}
                      label={t('nav.books', 'Books')}
                      to="/books"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Estimates · Delivery Challans · Credit Notes — the paper
                      trail either side of an invoice. */}
                  {gate.billingDocs && (
                    <NavItem
                      icon={<FileText size={20} />}
                      label={t('nav.billing_documents', 'Billing Documents')}
                      to="/billing-documents"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {gate.expenses && (
                    <NavItem
                      icon={<Receipt size={20} />}
                      label={t('nav.expenses', 'Expenses')}
                      to="/expenses"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {gate.suppliers && (
                    <NavItem
                      icon={<Briefcase size={20} />}
                      label={t('nav.suppliers', 'Suppliers')}
                      to="/suppliers"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {gate.suppliers && (
                    <NavItem
                      icon={<PackagePlus size={20} />}
                      label={t('nav.purchases', 'Purchases')}
                      to="/purchases"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* P&L · GSTR-1 · Goals · Commerce as one tabbed destination. */}
                  {gate.reports && (
                    <NavItem
                      icon={<FileBarChart size={20} />}
                      label={t('nav.reports', 'Reports')}
                      to="/reports"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                </>
              )}
              {objectItems('money')}
          </NavGroup>
          )}

          {/* ══ STORE & SETUP ══ — the public website, the workshop behind it,
              and everything about the account itself. Website / Themes / Pages
              / SEO / Analytics each carry the canAccessArea('settings') check
              that the old Store group applied around them. */}
          <NavGroup
            id="store"
            title={t('nav.group.store', 'Store & Setup')}
            // `/approvals` is deliberately absent below: the bell owns it now,
            // so this group must not force itself open on that route.
            paths={["/design", "/template-builder", "/visual-editor", "/TemplateThemes", "/store-content", "/seo-settings", "/analytics", "/production", "/karigars", "/Profile", "/portfolio", "/store-settings", "/tools", "/personalize", "/team", "/documents", "/Contact", ...objectPaths('store')]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              {/* Every designer needs a way into their own editor to change
                  their words and pictures. Themes was the only door before, and
                  it is now admin-only — without this they had none. */}
              {gate.website && (
                <NavItem
                  icon={<Paintbrush size={20} />}
                  label={t('nav.website', 'Website')}
                  to="/design"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Themes = choosing the store's template, which is the platform
                  admin's call. Hidden rather than shown-and-refused. */}
              {gate.themes && (
                <NavItem
                  icon={<LayoutTemplate size={20} />}
                  label={t('nav.themes', 'Themes')}
                  to="/TemplateThemes"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Pages on/off, contact details, policies, legal wording — the
                  words the storefront says, owned by the shop not by us. */}
              {gate.website && (
                <NavItem
                  icon={<FileText size={20} />}
                  label={t('nav.pages_content', 'Pages & Content')}
                  to="/store-content"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {gate.website && (
                <NavItem
                  icon={<Globe size={20} />}
                  label={t('nav.seo', 'SEO')}
                  to="/seo-settings"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {gate.analytics && (
                <NavItem
                  icon={<BarChart3 size={20} />}
                  label={t('nav.analytics', 'Analytics')}
                  to="/analytics"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {pluginsLoading ? (
                <GroupSkeleton rows={2} />
              ) : (
                <>
                  {gate.productionBoard && (
                    <NavItem
                      icon={<Hammer size={20} />}
                      label={t('nav.production', 'Production')}
                      to="/production"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* "Karigars", not "Team" — Team & Roles below is the staff
                      logins screen, and two entries reading "Team" that led to
                      different places was a coin toss every time. */}
                  {gate.karigars && (
                    <NavItem
                      icon={<UserCog size={20} />}
                      label={t('nav.artisans', `${capWord('worker', 'Artisan')}s`)}
                      to="/karigars"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                </>
              )}
              <NavItem
                icon={<CircleUserRound size={20} />}
                label={t('nav.brand_profile', 'Brand Profile')}
                to="/Profile"
                currentPath={location.pathname}
                closeMobileMenu={closeMenuIfOpen}
              />
              {/* Portfolio / public link-hub — switchable since Jul 2026, so a
                  solo shopkeeper isn't handed a link-hub they never asked for.
                  Every EXISTING business has it on via its preset.

                  Labelled "Link in Bio", not "Portfolio": the old name came
                  from the model behind it rather than from what it is — a
                  shareable page of links (WhatsApp, menu, maps, socials). Sat
                  under Store & Setup beside Website and Themes, where it read
                  as a second place to design your site, and it collided with
                  the creative trade's real work portfolio ("My Work"). */}
              {gate.portfolio && (
                <NavItem
                  icon={<Share2 size={20} />}
                  label={t('nav.link_in_bio', 'Link in Bio')}
                  to="/portfolio"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Shipping, returns, COD, welcome coupon — all storefront things. */}
              {gate.storeSettings && (
                <NavItem
                  icon={<Settings size={20} />}
                  label={t('nav.store_settings', 'Store Settings')}
                  to="/store-settings"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Tools — the owner's compose surface for the whole feature set,
                  and the ONLY way to switch a capability on once /setup has been
                  completed. Owner-only, matching the server. */}
              {gate.features && (
                <NavItem
                  icon={<Blocks size={20} />}
                  label={t('nav.features', 'Features')}
                  to="/tools"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {gate.personalize && (
                <NavItem
                  icon={<Wand2 size={20} />}
                  label={t('nav.personalize', 'Personalize')}
                  to="/personalize"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Team & Roles — owner-only, and hidden until the business says
                  it actually has staff (asked at signup, switchable in Tools). */}
              {gate.teamRoles && (
                <NavItem
                  icon={<UsersRound size={20} />}
                  label={t('nav.team_roles', 'Team & Roles')}
                  to="/team"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Document Locker — owner/manager only (sensitive papers), and
                  switchable since Jul 2026 so it isn't forced on a new business. */}
              {/* Labelled "Licences & Files", not "Documents": that was one
                  word away from "Billing Documents" in the Money group — which
                  is estimates, challans and credit notes and has nothing to do
                  with this. Its own file calls it the Document Locker: GST
                  certificates, licences, PAN, agreements. Named for what is
                  inside it. */}
              {gate.documents && (
                <NavItem
                  icon={<FolderLock size={20} />}
                  label={t('nav.licences_files', 'Licences & Files')}
                  to="/documents"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Trash — deleting a product or a customer is now recoverable
                  for 30 days, and a Trash nobody can find is a Trash nobody
                  trusts. Gated on the SETTINGS area, mirroring the server's
                  `CanAccessSettings` exactly: owner and manager, not a cashier.
                  Anything looser shows a screen whose every button 403s.

                  It is a nav item rather than a hub tab because it spans
                  products AND customers and so belongs to no single hub. */}
              {canAccessArea('settings') && (
                <NavItem
                  icon={<Trash2 size={20} />}
                  label={t('nav.trash', 'Trash')}
                  to="/trash"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Approvals is NOT a nav item any more (UI_CONSOLIDATION 4B):
                  it is the bell in the top bar, where a queue that needs a
                  decision TODAY belongs — a nav item can't tell you there are
                  three waiting. `/approvals` is still routed and still renders
                  the full list; the bell links to it, and so does the command
                  palette (both behind the same `cap:approvals` gate). */}
              <NavItem
                icon={<Mail size={20} />}
                label={t('nav.support', 'Support')}
                to="/Contact"
                currentPath={location.pathname}
                closeMobileMenu={closeMenuIfOpen}
              />
              {objectItems('store')}
          </NavGroup>
          </>
          )}

          {/* Shown only while filtering, when nothing matched. Without it the
              nav is simply blank and reads as broken. */}
          {filter && (
            <p className="sidebar-no-match hidden px-3 py-6 text-[12.5px] text-gray-500">
              {t('nav.no_match', 'Nothing matches “{q}”.', { q: navQuery.trim() })}
            </p>
          )}
        </nav>
        </SidebarFilterContext.Provider>

        <div className="p-4 border-t border-line shrink-0">
          <button
            onClick={() => {
              logoutUser();
              if (isMobileMenuOpen) setIsMobileMenuOpen(false);
            }}
            className="flex items-center space-x-3 w-full px-3 py-2.5 rounded-[10px] text-gray-600 hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#DC2626] focus-visible:bg-red-50 focus-visible:text-red-600 transition-all duration-200 group"
          >
            <LogOut size={20} className="text-gray-400 group-hover:text-red-600 transition-colors" />
            <span className="text-sm font-medium">{t('nav.logout', 'Log out')}</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
}

// Collapsible sidebar section. The header toggles the group; a group whose
// `paths` contain the current route is forced open so the active item is
// never hidden.
function NavGroup({ id, title, paths = [], currentPath, collapsedMap, onToggle, children }) {
  const filter = useContext(SidebarFilterContext);
  const containsActive = paths.some((path) => currentPath === path || currentPath.startsWith(`${path}/`));
  // While filtering, every group is open — a match must never sit behind a
  // collapsed chevron. `data-navgroup` lets CSS hide a group whose items all
  // filtered themselves out (see `[data-navgroup]:not(:has(...))` in index.css).
  const isCollapsed = !filter && !!collapsedMap[id] && !containsActive;
  return (
    <div className="mt-5" data-navgroup>
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={!isCollapsed}
        className="w-full flex items-center justify-between px-3 mb-2 focus:outline-none group/hdr"
      >
        <span className="font-display text-[10.5px] font-bold uppercase tracking-[0.14em] text-gray-500 group-hover/hdr:text-ink transition-colors">
          {title}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-300 group-hover/hdr:text-gray-500 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
        />
      </button>
      {!isCollapsed && <div className="space-y-1">{children}</div>}
    </div>
  );
}

function NavItem({ icon, label, to, currentPath, closeMobileMenu, disabled, onDisabledClick, badge }) {
  const filter = useContext(SidebarFilterContext);
  const isActive = currentPath === to;

  // Filtering happens here rather than in a central list because the nav is
  // authored as inline JSX — each item is the only thing that knows its label.
  // `label` can be a string or a template result; String() keeps it safe.
  if (filter && !String(label ?? '').toLowerCase().includes(filter)) return null;

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      if (onDisabledClick) {
        onDisabledClick();
      }
    } else {
      if (closeMobileMenu) closeMobileMenu();
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      data-navitem
      aria-current={isActive ? "page" : undefined}
      className={`relative flex items-center justify-between px-3 py-2.5 rounded-[10px] transition-all duration-200 group focus:outline-none ${
        isActive
          ? 'bg-orange-50 text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]'
          : 'text-ink hover:bg-gray-100 hover:text-ink focus-visible:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-primary"
        />
      )}
      <div className="flex items-center space-x-3">
        <span className={`transition-colors duration-200 ${
          isActive
            ? 'text-primary'
            : 'text-gray-400 group-hover:text-gray-600'
        }`}>
          {icon}
        </span>
        <span className={`text-[15px] font-medium transition-colors duration-200 ${
            isActive ? 'text-primary' : 'text-ink'
        }`}>
          {label}
        </span>
      </div>
      {badge && <span className="ml-auto">{badge}</span>}
    </Link>
  );
}
